import React from "react";
import "./OutletPage.css";
import { CheckCircle, ArrowRight, Play } from "lucide-react";

import heroImg      from "../image/gen-restaurant-hero.png";
import tablesImg    from "../image/gen-restaurant-tables.png";
import kitchenImg   from "../image/gen-restaurant-kitchen.png";
import captainImg   from "../image/gen-restaurant-captain.png";
import staffImg     from "../image/gen-restaurant-staff.png";
import inventoryImg from "../image/gen-restaurant-inventory.png";
import gstImg       from "../image/gen-restaurant-gst.png";
import demoImg      from "../image/DemoForm_img.png";

const ACCENT   = "#f97316";
const GRADIENT = "linear-gradient(135deg, #f97316, #ea580c)";
const GLOW     = "rgba(249,115,22,0.3)";

const css = `
  .op-restaurant { --op-accent:#f97316; --op-gradient:${GRADIENT}; --op-glow:${GLOW};
    --op-accent-10:rgba(249,115,22,0.1); --op-accent-30:rgba(249,115,22,0.3);
    --op-accent-05:rgba(249,115,22,0.05); }
`;

export default function RestaurantOutletPage() {
  return (
    <div className="op-page op-restaurant">
      <style>{css}</style>

      {/* ── HERO ── */}
      <section className="op-hero">
        <div className="op-hero__bg">
          <img src={heroImg} alt="Restaurant POS" />
          <div className="op-hero__gradient" />
          <div className="op-hero__noise" />
        </div>
        <div className="op-hero__content">
          <div className="op-hero__eyebrow">
            <span className="op-hero__eyebrow-dot" />
            Fine Dine &amp; Restaurant POS
          </div>
          <h1 className="op-hero__title">
            Every table served,<br />
            <span className="op-accent-text">every bill perfect</span>
          </h1>
          <p className="op-hero__subtitle">
            From table assignment to KOT to final payment — KadaiPOS gives your
            restaurant complete control. No missed orders, no billing errors,
            no chaos.
          </p>
          <div className="op-hero__actions">
            <button
              className="op-btn-primary"
              onClick={() => document.getElementById("op-demo")?.scrollIntoView({ behavior: "smooth" })}
            >
              Get Free Demo <ArrowRight size={18} />
            </button>
            <button className="op-btn-ghost">
              <Play size={16} /> See It In Action
            </button>
          </div>
          <div className="op-hero__stats">
            <div className="op-hero__stat-item">
              <span className="op-hero__stat-val">100+</span>
              <span className="op-hero__stat-label">Tables Managed</span>
            </div>
            <div className="op-hero__stat-item">
              <span className="op-hero__stat-val">&lt;2s</span>
              <span className="op-hero__stat-label">KOT Print Speed</span>
            </div>
            <div className="op-hero__stat-item">
              <span className="op-hero__stat-val">Live</span>
              <span className="op-hero__stat-label">Table Status</span>
            </div>
            <div className="op-hero__stat-item">
              <span className="op-hero__stat-val">Split</span>
              <span className="op-hero__stat-label">Bill Support</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── BENTO FEATURES ── */}
      <section className="op-bento">
        <div className="op-bento__header">
          <div className="op-section-eyebrow">What's Inside</div>
          <h2 className="op-section-title">Built for the dining floor</h2>
          <p className="op-section-subtitle">
            Every feature your waitstaff, kitchen team and manager need — all talking to each other in real time.
          </p>
        </div>

        <div className="op-bento__grid">
          {/* Accent stat card */}
          <div className="op-card op-card--narrow op-card--accent">
            <div className="op-card__body" style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <div style={{ fontSize: "3rem", marginBottom: "16px" }}>🍽️</div>
              <h3 className="op-card__title" style={{ color: "#fff" }}>Table Management</h3>
              <p className="op-card__desc" style={{ color: "rgba(255,255,255,0.75)" }}>
                Assign, merge, move and split tables instantly. See every table's status at a glance — occupied, billed, or free.
              </p>
              <ul className="op-card__list">
                <li>Visual floor plan with live table status</li>
                <li>Merge tables for large groups in one tap</li>
                <li>Transfer orders between tables seamlessly</li>
              </ul>
            </div>
          </div>

          {/* Wide – KOT */}
          <div className="op-card op-card--wide">
            <img src={kitchenImg} alt="KOT System" className="op-card__img" />
            <div className="op-card__body">
              <div className="op-card__tag">🖨️ Kitchen Order Tickets</div>
              <h3 className="op-card__title">Order sent to kitchen in under 2 seconds</h3>
              <p className="op-card__desc">
                As soon as the waiter confirms an order on the billing screen, a KOT prints at the respective kitchen station — no shouting, no errors, no duplicates.
              </p>
              <ul className="op-card__list">
                <li>Station-wise KOT routing (grill, tandoor, cold section)</li>
                <li>Modification KOTs auto-highlighted in red</li>
                <li>KOT cancellation with reason code and log</li>
                <li>Kitchen Display System (KDS) compatible</li>
              </ul>
            </div>
          </div>

          {/* Half – Tables image */}
          <div className="op-card op-card--half">
            <img src={tablesImg} alt="Table Layout" className="op-card__img op-card__img--tall" />
            <div className="op-card__body">
              <div className="op-card__tag">🗂️ Floor Management</div>
              <h3 className="op-card__title">Your entire floor on one screen</h3>
              <p className="op-card__desc">Multiple dining areas, outdoor seating, private rooms — manage them all from a single layout view.</p>
            </div>
          </div>

          {/* Half – Captain App */}
          <div className="op-card op-card--half">
            <img src={captainImg} alt="Captain App" className="op-card__img op-card__img--tall" />
            <div className="op-card__body">
              <div className="op-card__tag">📱 Captain App</div>
              <h3 className="op-card__title">Order from tableside — no POS needed</h3>
              <p className="op-card__desc">Your waiter walks up, takes order on the Android app, and it fires directly to kitchen and billing. No paper, no running back.</p>
            </div>
          </div>

          {/* Full – GST Reports */}
          <div className="op-card op-card--full" style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
            <div className="op-card__body" style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <div className="op-card__tag">📊 GST &amp; Tax Reports</div>
              <h3 className="op-card__title">File GST without an accountant</h3>
              <p className="op-card__desc">
                KadaiPOS auto-calculates CGST, SGST, IGST on every bill. Export day-end or month-end reports with one click — ready for your CA or Tally import.
              </p>
              <ul className="op-card__list">
                <li>HSN-code-wise tax breakup per item</li>
                <li>Automatic tax calculation on discounts &amp; complimentary</li>
                <li>GSTR-1 &amp; GSTR-3B compatible exports</li>
                <li>Multi-outlet consolidated tax reports</li>
              </ul>
            </div>
            <img src={gstImg} alt="GST Reports" className="op-card__img" style={{ height: "100%", minHeight: 260 }} />
          </div>
        </div>
      </section>

      {/* ── DEEP DIVE ── */}
      <section className="op-deepdive">
        <div className="op-deepdive__inner">
          <div className="op-bento__header">
            <div className="op-section-eyebrow">Staff &amp; Inventory</div>
            <h2 className="op-section-title">Run a tight ship — front and back</h2>
          </div>

          <div className="op-deepdive__grid">
            <div className="op-deepdive__img-wrap">
              <img src={staffImg} alt="Employee Management" />
              <div className="op-deepdive__img-badge">👤 Employee Reports</div>
            </div>
            <div className="op-deepdive__text">
              <h3>Know exactly what each staff member is doing</h3>
              <p>
                Track table-wise orders taken, cash collected, voids and discounts applied per biller. Role-based access keeps sensitive features locked to managers only.
              </p>
              <ul className="op-check-list">
                {[
                  "Individual biller sales &amp; order reports",
                  "Role-based access control (manager / waiter / cashier)",
                  "Attendance &amp; shift management",
                  "Discount approval workflow — manager PIN required",
                  "Audit trail for every bill void or modification"
                ].map((t, i) => (
                  <li key={i}>
                    <span className="op-check-icon"><CheckCircle size={13} /></span>{" "}
                    <span dangerouslySetInnerHTML={{ __html: t }} />
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="op-deepdive__grid op-deepdive__grid--reverse" style={{ marginTop: 60 }}>
            <div className="op-deepdive__img-wrap">
              <img src={inventoryImg} alt="Inventory Management" />
              <div className="op-deepdive__img-badge">📦 Inventory Control</div>
            </div>
            <div className="op-deepdive__text">
              <h3>Never run out of Butter Chicken mid-service again</h3>
              <p>
                Real-time ingredient tracking deducts stock as each dish is ordered. Low-stock alerts appear on the billing screen before your team runs out.
              </p>
              <ul className="op-check-list">
                {[
                  "Recipe-based auto-deduction per dish sold",
                  "Low stock alerts on billing screen during service",
                  "Wastage entry with reason (spoilage / staff meal / event)",
                  "Supplier purchase orders raised from POS",
                  "Opening &amp; closing stock reports daily"
                ].map((t, i) => (
                  <li key={i}>
                    <span className="op-check-icon"><CheckCircle size={13} /></span>{" "}
                    <span dangerouslySetInnerHTML={{ __html: t }} />
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── MINI STRIP ── */}
      <section className="op-strip">
        <div className="op-strip__grid">
          {[
            { icon: "🔔", title: "Waiter Calling System", desc: "Table-side devices let guests call for a waiter, request water, or ask for the bill — all tracked on the POS." },
            { icon: "✂️", title: "Split Bills", desc: "Split by item, by person, or by amount. Multiple payment modes on a single bill — no calculator needed." },
            { icon: "📱", title: "Online Orders", desc: "Accept Swiggy, Zomato and your own website orders directly on the same POS screen as dine-in." },
            { icon: "📈", title: "Live Dashboard", desc: "Hourly covers, average order value, top-selling dishes, and revenue — all on a real-time manager dashboard." },
          ].map((f, i) => (
            <div className="op-mini-card" key={i}>
              <div className="op-mini-card__icon">{f.icon}</div>
              <div className="op-mini-card__title">{f.title}</div>
              <p className="op-mini-card__desc">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="op-cta" id="op-demo">
        <div className="op-cta__inner">
          <div className="op-cta__left">
            <h2>Get KadaiPOS running in your restaurant this week</h2>
            <p>Quick setup — no technical skills needed. Our team installs, configures your menu, and trains your staff on Day 1. You're taking orders faster by Day 2.</p>
            <div className="op-cta__form">
              <input className="op-cta__input" placeholder="Your name *" />
              <div className="op-cta__row">
                <input className="op-cta__input" placeholder="Phone number *" />
                <input className="op-cta__input" placeholder="City *" />
              </div>
              <input className="op-cta__input" placeholder="Restaurant name *" />
              <button className="op-cta__submit">Book Free Demo →</button>
            </div>
          </div>
          <div className="op-cta__right-img">
            <img src={demoImg} alt="Schedule Demo" />
          </div>
        </div>
      </section>
    </div>
  );
}
